import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { SbSharedFeaturedDeal } from '../SbSharedFeaturedDeal/SbSharedFeaturedDeal.tsx';
import { SbSharedL1ModuleTile } from '../SbSharedL1ModuleTile/SbSharedL1ModuleTile.tsx';
import { SbSharedL1ModuleBanner } from '../SbSharedL1ModuleBanner/SbSharedL1ModuleBanner.tsx';
import { SbRespFeaturedTrendingLazy } from '../SbRespFeaturedTrending/SbRespFeaturedTrendingLazy.tsx';
import { shopVertical } from '../sbMessages.tsx';

// types
import { type SbRespL1Landing_itemSearch$key as ItemSearch } from './__generated__/SbRespL1Landing_itemSearch.graphql.ts';

//styles
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbRespL1Landing.scss';

type Props = {
    itemSearch: ItemSearch;
};

const itemSearchFragment = graphql`
    fragment SbRespL1Landing_itemSearch on ItemSearchQueryConnection {
        ...SbSharedFeaturedDeal_itemSearch
        topCategoryL1
        meta {
            header
            subHeader
        }
        l1Module(previewId: $previewId) {
            ...SbSharedL1ModuleBanner_l1Module
            ...SbSharedFeaturedDeal_l1Module
            secondTile {
                ...SbSharedL1ModuleTile_l1ModuleItemTile
            }
        }
    }
`;

export const SbRespL1Landing: FC<Props> = ({ itemSearch: itemSearchRef }) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);

    const { meta, topCategoryL1, l1Module } = itemSearch || {};
    const { header, subHeader } = meta || {};

    if (!topCategoryL1) {
        return null;
    }

    const shopVerticalHeading = shopVertical[topCategoryL1] || null;

    return (
        <>
            <div
                className={classnames(
                    dibsCss.wFull,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.flexCol
                )}
            >
                <div className={classnames(dibsCss.textCenter, dibsCss.mbMedium)}>
                    <h1
                        className={classnames(dibsCss.m0, dibsCss.sassyFontHeaderSizeXLarge)}
                        data-tn="header-title"
                    >
                        {header}
                    </h1>
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(dibsCss.m0, dibsCss.sassyFontBody)}
                                dataTn="header-sub-title"
                            >
                                {subHeader}
                            </Heading>
                        )}
                    </HeadingLevel>
                </div>
                {l1Module && (
                    <div
                        className={classnames(
                            dibsCss.wFull,
                            dibsCss.flex,
                            dibsCss.justifyBetween,
                            dibsCss.mbLarger
                        )}
                    >
                        <SbSharedFeaturedDeal itemSearch={itemSearch} l1Module={l1Module} />
                        <SbSharedL1ModuleBanner l1Module={l1Module} />
                        <SbSharedL1ModuleTile
                            l1ModuleItemTile={l1Module?.secondTile || null}
                            creative="Right Tile"
                        />
                    </div>
                )}
            </div>
            <div
                className={classnames(
                    styles.lowerModuleContainer,
                    dibsCss.mbLarge,
                    dibsCss.bgMoonstone
                )}
            >
                <div
                    className={classnames(
                        styles.lowerModule,
                        dibsCss.flex,
                        dibsCss.maxW1440px,
                        dibsCss.my0,
                        dibsCss.mxAuto
                    )}
                >
                    <SbRespFeaturedTrendingLazy vertical={topCategoryL1} />
                </div>
            </div>

            {shopVerticalHeading && (
                <HeadingLevel>
                    {Heading => (
                        <Heading
                            className={classnames(dibsCss.textCenter, dibsCss.sassyFontHeader)}
                        >
                            {shopVerticalHeading}
                        </Heading>
                    )}
                </HeadingLevel>
            )}
        </>
    );
};
